// lib/queryClient.ts
import { MutationCache, QueryClient } from "@tanstack/react-query";
import { notify } from "@/lib/notify";

/**
 * Declarative mutation feedback:
 *   useMutation({ mutationFn, meta: { successMessage: "Rule saved" } })
 *
 * - `successMessage` → success toast after the mutation resolves.
 * - Failures are toasted automatically unless the mutation declares its own
 *   `onError` (it's then responsible for surfacing the error) or sets
 *   `errorToast: false` (e.g. errors shown inside a still-open dialog).
 */
interface AppMutationMeta extends Record<string, unknown> {
  successMessage?: string;
  errorToast?: boolean;
  /** Fallback text when the API error carries no message. */
  errorMessage?: string;
}

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: AppMutationMeta;
  }
}

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onSuccess: (_data, _vars, _ctx, mutation) => {
      const message = mutation.meta?.successMessage;
      if (message) notify.success(message);
    },
    onError: (error, _vars, _ctx, mutation) => {
      if (mutation.meta?.errorToast === false) return;
      if (mutation.options.onError) return;
      notify.error(error, mutation.meta?.errorMessage ?? "The action failed. Please try again.");
    },
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 1000 * 60, // 1 min
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});
